import { useEffect, useRef, useState, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Input } from '@heroui/react';
import { Bars3Icon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DashboardSidebar } from '@components/sidebar/DashboardSidebar';
import { StoreManagerPanel } from '@components/platform/tasks/StoreManagerPanel';
import { NotificationManager } from '@components/common/NotificationManager';
import { PublishSignalExecutor } from '@components/agent-chat/ws-chat/PublishSignalExecutor';
import { AppReviewPopup } from '@components/common/AppReviewPopup';
import { useAppReviewNotification } from '@hooks/useAppReviewNotification';
import { useHandoffSummarySource } from '@hooks/useHandoffSummarySource';
import { useThemeStore } from '@stores/themeStore';
import { useIsMobile } from '@hooks/useMediaQuery';
import { useSafeViewportHeight } from '@hooks/useSafeViewportHeight';
import { getUnlockedAgentSlugs, effectivePlanStatus } from '@lib/services/payment/planUtils';
import { useAuthStore } from '@stores/authStore';
import { usePaymentStore } from '@stores/paymentStore';
import HomeBgImage from '../assets/Home_bg.png';
import YepAILogo from '../assets/logo.png';
import { ShopifyDisconnectedModal, ShopifySyncingOverlay } from '@components/shopify/ShopifyConnectionGuards';
import { ReadOnlyBar } from '@components/billing/ReadOnlyBar';
import { PlatformTrialController } from '@components/billing/PlatformTrialController';

// Navigation items for search.
// requiresAnna: 目的地是 Anna 的配置，没解锁就不该出现在结果里。
const searchableNavItems: Array<{ name: string; href: string; requiresAnna?: boolean }> = [
  { name: 'Home', href: '/' },
  // Knowledge Base kept its search entry but points at the Settings Hub card rather than the retired
  // route, for the same reason the analytics sheets below do: the merchant still thinks in terms of
  // "Knowledge Base", and sending search through a redirect would flash the wrong screen on the way.
  { name: 'Knowledge Base', href: '/platform/settings#knowledge-base' },
  // Flows has NO entry here. It is not a destination any more — it is a panel inside Anna's card, and
  // the anchor can only name the card, so a "Flows" result would have landed the merchant on Widget
  // Appearance. Settings already carries it; a search hit that opens the wrong setting is worse than
  // no hit at all.
  // Preview & Test 是 Anna 对话区的抽屉，不再是一个页面。指向带 ?preview=1 的 Anna Chat
  // 而不是老路径：这个文件的规矩是直接指向目的地，不让搜索走一次重定向。
  { requiresAnna: true, name: 'Preview & Test', href: '/ai-team/anna/chat?preview=1' },
  // Product Training 的侧边栏入口收走了，但页面还在（Settings 里没有等价物）。
  // 搜索这一条必须留着——否则它就成了只能手敲 URL 才能到的孤儿页：知识库那张
  // ConnectedSourceCard 虽然会跳过去，可它自己所在的 /knowledges 也没有导航入口。
  { requiresAnna: true, name: 'Product Training', href: '/ai-training/product' },
  { requiresAnna: true, name: 'Customisation', href: '/platform/settings#customer-settings' },
  { name: 'Leads', href: '/customers/leads' },
  // 客户对话在 Anna 页左栏，不再有独立页面
  { requiresAnna: true, name: 'Conversation History', href: '/ai-team/anna/chat' },
  // The Customer analytics sheets moved into Home's Customer Data tab (PRD #2). Search points
  // straight at each sheet rather than at the old /analytics/* routes — those still redirect, but
  // sending search through a redirect would flash the wrong screen on the way.
  { name: 'Customer Data', href: '/home/support' },
  { name: 'Support Performance', href: '/home/support?sheet=performance' },
  { name: 'Chat Summary', href: '/home/support?sheet=chat-summary' },
  { name: 'Busiest Time', href: '/home/support?sheet=busiest-time' },
  { name: 'SEO Insights', href: '/home/seo' },
  { name: 'Setup Guide', href: '/onboarding' },
  // Integrations points at the Settings Hub card, not /integrations: the card is the overview now, and
  // the pages under /integrations are what its Setup links INTO. Searching "Integrations" should land on
  // the list of channels, not on one channel's authorisation screen.
  { name: 'Integrations', href: '/platform/settings#integrations' },
  { name: 'Subscription & Billing', href: '/settings/subscription' },
  // 指向 Settings Hub 的 Integrations 卡而不是 /settings/config——后者渲染的正是
  // 同一个 LiveChatAgentSetup，留着就是绕过 Hub 的第二扇门。
  { requiresAnna: true, name: 'Live Chat Settings', href: '/platform/settings?setup=whatsapp#integrations' },
];

export function DashboardLayout() {
  const { sidebarCollapsed, initializeSidebar, setSidebarCollapsed, searchQuery, setSearchQuery } =
    useThemeStore();
  const location = useLocation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  // 与侧边栏同一套判断：那边用 unlockedAgents.has('anna') 决定 Customer 组锁不锁，
  // 搜索结果不该比它宽松。
  const user = useAuthStore(state => state.user);
  const cohort = usePaymentStore(state => state.cohort);
  const unlockedAgents = useMemo(
    () => getUnlockedAgentSlugs(effectivePlanStatus(user), cohort),
    [user, cohort]
  );
  const isMobile = useIsMobile();
  const safeHeight = useSafeViewportHeight();

  // CS 2.0 需求 13: the handoff red-dot count, kept fresh from here — the one place that is mounted on
  // every dashboard page — and read by the sidebar, Home and Anna Chat. Only for a plan with Anna:
  // without her there is no one to hand off from, and the sidebar draws her group locked.
  useHandoffSummarySource(unlockedAgents.has('anna') ? user?.id : undefined);

  // App review notification hook
  const { showReviewPopup, currentNotification, onClosePopup, onSubmitRating } =
    useAppReviewNotification();

  // Filter navigation items based on search query
  const filteredNavItems = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return searchableNavItems
      .filter(item => item.name.toLowerCase().includes(query))
      // Anna 专属的目的地按套餐过滤。没买 Anna 的商家点进去，Settings Hub 里那张卡
      // 根本不渲染——给一个到不了的入口，比没有入口更让人以为是坏了。
      .filter(item => !item.requiresAnna || unlockedAgents.has('anna'));
  }, [searchQuery, unlockedAgents]);

  // Handle search result click
  const handleSearchResultClick = (href: string) => {
    navigate(href);
    setSearchQuery('');
    setShowMobileSearch(false);
  };

  // Check if current route is conversation-logs
  // All agent workspace / chat pages (e.g. /ai-team/anna, /ai-team/alex,
  // /ai-team/marketing/chat) manage their own full-height layout + internal
  // scroll, so the layout must not add its own padding / outer scroll / cap
  // (otherwise you get nested scrollbars). Excludes the /ai-team landing page.
  const isAgentWorkspaceRoute = /^\/ai-team\/.+/.test(location.pathname);

  useEffect(() => {
    initializeSidebar();
  }, [initializeSidebar]);

  // A layout can cross the desktop/mobile breakpoint after mount (orientation
  // change, split-screen, DevTools). Collapse once on that transition so the
  // desktop sidebar does not arrive as a blocking mobile drawer.
  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  }, [isMobile, setSidebarCollapsed]);

  // Scroll to top of the scrollable container on route change
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobile && !sidebarCollapsed) {
      // Lock scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      // Restore scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isMobile, sidebarCollapsed]);

  return (
    <div
      className="relative h-screen w-full overflow-hidden"
      style={isMobile ? { height: safeHeight } : undefined}
    >
      {/* Full screen background */}
      <div
        className="absolute inset-0 bg-cover bg-top bg-no-repeat"
        style={{
          backgroundImage: `url(${HomeBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'top',
        }}
      />

      {/* Background overlay for better contrast */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/30 to-pink-900/20" />

      {/* Main layout container */}
      <div className="relative z-10 flex h-full">
        {/* Mobile backdrop overlay - white frosted glass, closes sidebar when clicked */}
        {!sidebarCollapsed && (
          <div
            className="lg:hidden fixed inset-0 z-[55]"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            onClick={() => setSidebarCollapsed(true)}
          />
        )}

        {/* Sidebar - Hidden on mobile, overlay on tablet */}
        <div
          className={`${sidebarCollapsed ? 'hidden lg:block overflow-visible' : 'fixed lg:relative'} z-[60] lg:z-auto`}
          style={{ overflow: sidebarCollapsed ? 'visible' : undefined }}
        >
          <DashboardSidebar />
        </div>

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* [SDD T3] Permanent "workspace is read-only" bar (PRD V6.0 ②) — the top
              of this column, ABOVE <main> and outside its scroll container, so it
              shows on every page (including agent-workspace routes) and never
              scrolls away. Renders nothing itself unless the platform trial has
              lapsed without approval. */}
          <ReadOnlyBar />

          {/* Mobile Header - in the gradient area, always show on mobile */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3 relative z-[55]">
            {/* Menu Button */}
            <button
              type="button"
              aria-label="Open navigation menu"
              onClick={() => {
                setShowMobileSearch(false);
                setSidebarCollapsed(false);
              }}
              className="flex h-11 w-11 items-center justify-center rounded-lg"
            >
              <Bars3Icon className="h-5 w-5 text-white" />
            </button>

            {/* Logo */}
            <div className="flex items-center">
              <img src={YepAILogo} alt="YepAI" className="h-8 w-auto" />
            </div>

            {/* Search Button */}
            <button
              type="button"
              aria-label={showMobileSearch ? 'Close search' : 'Open search'}
              onClick={() => {
                setShowMobileSearch(!showMobileSearch);
                if (showMobileSearch) {
                  setSearchQuery('');
                }
              }}
              className="flex h-11 w-11 items-center justify-center rounded-lg"
            >
              {showMobileSearch ? (
                <XMarkIcon className="h-5 w-5 text-white" />
              ) : (
                <MagnifyingGlassIcon className="h-5 w-5 text-white" />
              )}
            </button>
          </div>

          {/* Mobile Search Input - below header */}
          {showMobileSearch && (
            <div className="lg:hidden px-4 pb-3 relative z-[70]">
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onBlur={() => {
                  // Use timeout to allow click events on search results to fire first
                  setTimeout(() => {
                    setShowMobileSearch(false);
                    setSearchQuery('');
                  }, 150);
                }}
                autoFocus
                startContent={<MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />}
                classNames={{
                  base: 'border-white/20',
                  input: 'text-gray-900 placeholder:text-gray-500',
                  inputWrapper:
                    '!bg-white border-white/30 data-[hover=true]:!bg-white group-data-[focus=true]:!bg-white',
                }}
                size="sm"
              />
              {/* Search Results Dropdown */}
              {filteredNavItems.length > 0 && (
                <div className="absolute left-4 right-4 top-full mt-1 z-50 bg-white rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto">
                  {filteredNavItems.map((item, index) => (
                    <button
                      key={item.href}
                      onClick={() => handleSearchResultClick(item.href)}
                      className={`w-full px-4 py-3 text-left text-gray-700 text-sm hover:bg-gray-100 transition-colors ${
                        index !== filteredNavItems.length - 1 ? 'border-b border-gray-100' : ''
                      }`}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Mobile Search Mask Overlay - covers content area when search is active */}
          {showMobileSearch && (
            <div
              className="lg:hidden fixed left-0 right-0 bg-black/50 z-[65] touch-none"
              style={{
                top: '100px',
                height: `calc(${safeHeight} - 100px)`,
                overscrollBehavior: 'none',
              }}
              onClick={() => {
                setShowMobileSearch(false);
                setSearchQuery('');
              }}
              onTouchMove={e => e.preventDefault()}
            />
          )}

          {/* Main content with white background and rounded corners.
              `overflow-clip`, NOT `overflow-hidden` (WOR-1376). Both clip the same pixels, but
              `hidden` makes the element a SCROLL CONTAINER: it can be scrolled programmatically —
              by `scrollIntoView`, or by the browser bringing a newly focused element into view —
              while having no scrollbar for anyone to scroll it back with. One stray focus scroll
              therefore strands the whole page above the viewport, with the DOM intact, nothing
              thrown and the URL unchanged, until a reload. `clip` establishes no scroll container
              at all, so that cannot happen here again.
              The switch that triggered it is fixed at its own source too (see FlowSwitch); this is
              the layer that turned a harmless scroll into an unrecoverable blank page, and
              `flashSettingsCard`'s scrollIntoView can reach these same ancestors.
              `min-h-0` comes WITH that swap and is not optional: a flex item's automatic minimum
              size collapses to zero only for a scroll container, which is exactly what `clip` stops
              being. Without it `<main>` grows to its content's min-content height on any page taller
              than the viewport, the nested `h-full` scroller grows with it, and the overflow is
              clipped by the root instead of scrolled — trading this bug for a worse one.
              Written as `overflow-hidden` + a `supports-[overflow:clip]` override rather than plain
              `overflow-clip`: §9.3.1 lists Safari / iOS Safari 14+ as 重点测试, and `overflow: clip`
              needs Safari 16. On 14 and 15 the bare declaration is simply DISCARDED, leaving
              `overflow: visible` — no clipping at all, so the square white child would paint through
              `lg:rounded-tl-3xl`. The fallback keeps those browsers exactly where they are today
              (clipped, and still carrying the scroll-container trap) while everything newer gets the
              fix — and mobile Safari is the very place this bug was reported from. */}
          <main
            className={`flex-1 min-h-0 overflow-hidden supports-[overflow:clip]:[overflow:clip] ${
              isAgentWorkspaceRoute ? '' : 'lg:pt-6'
            }`}
          >
            <div
              className={`h-full bg-white overflow-hidden supports-[overflow:clip]:[overflow:clip] relative ${
                isAgentWorkspaceRoute ? '' : 'lg:rounded-tl-3xl shadow-2xl'
              }`}
            >
              <div
                ref={scrollContainerRef}
                className={`h-full bg-white scrollbar-thin ${
                  isAgentWorkspaceRoute ? 'overflow-hidden' : 'overflow-x-hidden overflow-y-auto'
                }`}
              >
                <div
                  className={`bg-white ${
                    isAgentWorkspaceRoute
                      ? 'h-full'
                      : 'p-3 sm:p-4 md:p-6 lg:p-8 xl:p-10 lg:min-h-full'
                  }`}
                >
                  {/* Full-width house rule: dashboard pages fill the content area
                      and left-align — no centered max-w-7xl column. */}
                  <div className={isAgentWorkspaceRoute ? 'h-full w-full' : 'w-full'}>

                    <Outlet />
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* [SDD T3] Day-15 platform-trial approval dialog (PRD V6.0 ①/③) — mounted
          once, globally, for the same reason ShopifyDisconnectedModal is below:
          it must show on every authenticated page, not just Home. No UI of its
          own besides the one ApprovePlanModal instance. */}
      <PlatformTrialController />

      {/* [Infra 1.5 #23] 全局弹窗，不分页面。断开时弹一次、可关，关掉后首页那条提示条才出现；
          每次重新进入应用重弹（store 不 persist），同一次会话内不重复弹。 */}
      <ShopifyDisconnectedModal />

      {/* [Infra 1.5 #24] 重连授权（模拟）后的首次同步全屏 loading；读单一数据源的 syncing。 */}
      <ShopifySyncingOverlay />

      {/* [Infra 1.5 #25] 店长面板遮罩：全局挂一次（任务中心 / Home 的入口共享同一面板）。 */}
      <StoreManagerPanel />

      {/* Mobile overlay when sidebar is open - only covers content area, not sidebar */}
      {!sidebarCollapsed && (
        <div
          className="fixed top-0 left-[280px] right-0 bottom-0 z-20 bg-black/30 lg:hidden"
          style={{ transform: 'translateZ(0)' }}
          onClick={() => {
            const { setSidebarCollapsed } = useThemeStore.getState();
            setSidebarCollapsed(true);
          }}
        />
      )}

      {/* Notification Manager */}
      <NotificationManager />

      {/* 无 UI。发布信号的兜底执行者必须活在**布局**里,不能活在 Maya 页面里:商家一切走页面
          就没人接那条 REQUEST_PUBLISH_EXECUTION,结果是不发、不报错、Maya 干等(见组件顶注)。 */}
      <PublishSignalExecutor />

      {/* App Review Popup */}
      <AppReviewPopup
        isOpen={showReviewPopup}
        onClose={onClosePopup}
        onSubmit={onSubmitRating}
        notificationId={currentNotification?.id}
      />
    </div>
  );
}
